import { Trans } from '@lingui/macro'
import { CheckOutlined, ClearOutlined } from '@mui/icons-material'
import { FilledInputProps, Grid, InputAdornment, InputProps, MenuItem, OutlinedInputProps, TextField, TextFieldProps, Typography } from '@mui/material'
import { ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { bilingualNumberFormat, getCurrentWords, PhoneCountryCodeFormat, PhoneFormat, PhoneFormatWithExtension } from 'app/views/common/Formats'
import { EMAIL_FORMAT_HELPER_TEXT, PHONE_NUMBER_FORMAT_HELPER_TEXT, URL_FORMAT_HELPER_TEXT } from 'app/views/forms/formTranslations'
import { isNumber } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import NumberFormat from 'react-number-format'
import { useSelector } from 'react-redux'
import { valueExistsTS } from 'utilsTS'
import { countHelperText } from '../FormElement'
import { ErrorLabel } from '../forms/ErrorLabel'
import { FormTopLabel } from '../forms/FormTopLabel'

interface TextFieldWithCustomControlProps {
  maxWords?: number
  maxInput?: number
  minInput?: number
  limit?: number
  required?: boolean
  currency?: boolean
  errorLabel?: React.ReactNode
  handleChange: (value: string) => void
  type?: 'text' | 'number' | 'currency' | 'countryCode' | 'phone' | 'phoneWithExtension' | 'url' | 'email'
  language?: 'en' | 'fr' | 'iu-cans'
  isTopLabel?: boolean // If true, label will be on top of the input, otherwise it will be inside the input
  innerRef?: React.RefObject<HTMLInputElement>
  showErrorWithoutTouch?: boolean
  helperArray?: Array<string | JSX.Element>
  actionIconButton?: React.ReactNode
  wordCount?: boolean
  multiline?: boolean
  topLabel?: React.ReactNode
}

/**
 * Convinience wrapper for Material UI Text Field connected to Formik
 * @category Common components
 * @subcategory Formik
 * @component
 * @returns {JSX.Element}
 * @property {string} name This is an id of Formiks field that the TextField is connected to
 */
export const TextFieldWithCustomControl = (
  {
    name,
    type = 'text',
    limit,
    topLabel,
    required = false,
    maxInput,
    minInput,
    maxWords,
    disabled = false,
    isTopLabel,
    innerRef,
    select = false,
    wordCount = false,
    variant = 'outlined',
    showErrorWithoutTouch = false,
    actionIconButton,
    children,
    multiline = false,
    value,
    handleChange,
    error = false,
    errorLabel,
    helperArray: helperArrayProp,
    ...props
  }: TextFieldProps & TextFieldWithCustomControlProps
): JSX.Element => {
  const user = useSelector<ReduxState, ReduxUser>(state => state.user)
  const [defaultZeroRemoved, setDefaultZeroRemoved] = useState(false)
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
  const [startEditingValue, setStartEditingValue] = useState<string | null>(null)
  const helperArray = Array.isArray(props.helperArray) ? [...props.helperArray] : []
  const [inputValue, setInputValue] = useState<string>(String(value))
  const [touched, setTouched] = useState(false)

  const language = props.language ?? user.language

  const inputChanged = (e, validateOnKeystroke): void => {
    let value = e.target.value
    if (value === inputValue) {
      return
    }
    let invalid = false
    if (isNumber(maxWords)) {
      const words = +getCurrentWords(e.target.value)
      if (words > maxWords) {
        invalid = true
      } else if (words === maxWords) {
        value = value.trim()
      }
    }
    if (!invalid) {
      setInputValue(value)
    } else {
      setInputValue(inputValue)
    }
    if (timer != null) {
      clearTimeout(timer)
    }
    const newTimer = setTimeout(() => {
      if (handleChange != null) {
        handleChange(value)
      }
    }, 700)
    setTimer(newTimer)
  }

  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  const NumberFormatWithMinAndMax = useCallback(
    (props) => {
      const { inputRef, id, value = '', onChange, ...other } = props
      let separator = ','
      if (language !== 'en' && type === 'currency') {
        separator = ' '
      }
      return (
        <NumberFormat
          {...other}
          isAllowed={({ floatValue }) => {
            if (!isNumber(floatValue)) {
              return true
            }
            if (valueExistsTS(maxInput)) {
              return floatValue <= Number(maxInput)
            }
            return true
          }}
          value={typeof props.value === 'object' ? '' : value}
          defaultValue=''
          thousandSeparator={separator}
          isNumericString={false}
          getInputRef={inputRef}
          allowNegative={false}
          suffix={language !== 'en' && type === 'currency' ? ' $' : ''}
          prefix={language === 'en' && type === 'currency' ? '$' : ''}
          allowLeadingZeros={false}
          onValueChange={(values) => {
            onChange({
              target: {
                name: props.name,
                value: values.value
              }
            })
          }}
        />
      )
    },
    [maxInput, minInput, type, language]
  )

  const baseProps: Partial<FilledInputProps> | Partial<OutlinedInputProps> | Partial<InputProps> = {}

  if (type === 'number' || type === 'currency') {
    baseProps.inputComponent = NumberFormatWithMinAndMax
  } else if (type.includes('phone')) {
    baseProps.inputComponent = type === 'phoneWithExtension'
      ? PhoneFormatWithExtension
      : PhoneFormat
    helperArray.push(<span>
      {PHONE_NUMBER_FORMAT_HELPER_TEXT}
      .
                     </span>)
  } else if (type === 'countryCode') {
    baseProps.inputComponent = PhoneCountryCodeFormat
    limit = 5
  } else if (type === 'url') {
    helperArray.push(<span>
      {URL_FORMAT_HELPER_TEXT}
      .
                     </span>)
  } else if (type === 'email') {
    helperArray.push(<span>
      {EMAIL_FORMAT_HELPER_TEXT}
      .
                     </span>)
  }

  if (valueExistsTS(wordCount) || valueExistsTS(maxWords)) {
    helperArray.push(
      <span key='wordsCount'>
        {getCurrentWords(inputValue, maxWords)} <Trans>words</Trans>
        {'. '}
      </span>
    )
  }

  if (type === 'number' || type === 'currency') {
    helperArray.push(<span key='numPerm'><Trans>
      Only numerical values are permitted in this field
                                         </Trans>
                     </span>)

    if (valueExistsTS(minInput)) {
      helperArray.push(
        <span key={1}>. </span>,
        <Trans>
          Minimum value is: {bilingualNumberFormat(minInput, language)}
        </Trans>
      )
    }
    if (valueExistsTS(maxInput)) {
      helperArray.push(
        <span key={1}>'. '</span>,
        <Trans>
          Maximum value is: {bilingualNumberFormat(maxInput, language)}
        </Trans>
      )
    }
  }

  let marginBottom: number | string = 0
  const style: React.CSSProperties = props.style ?? { marginTop: 5 }
  if (type === 'countryCode') {
    style.width = 95
  }
  if (valueExistsTS(style.marginBottom)) {
    marginBottom = style.marginBottom ?? style.margin ?? 0
    style.marginBottom = marginBottom
  }

  const inputHasValue = inputValue != null && inputValue !== ''
  let inputError = false
  if (isNumber(maxInput) && inputHasValue) {
    if (+maxInput < +inputValue) {
      inputError = true
    }
  }
  if (isNumber(minInput) && inputHasValue) {
    if (+minInput > +inputValue) {
      inputError = true
    }
  }
  if (isNumber(limit)) {
    if (+limit < inputValue.length) {
      inputError = true
    }
  }

  const showError = (error && (touched || showErrorWithoutTouch)) || inputError

  const toReturn = (
    <>
      {topLabel != null &&
        <FormTopLabel size='small' required={required}>
          {topLabel}
        </FormTopLabel>}
      <TextField
        name={name}
        value={inputValue}
        onChange={e => {
          if (props.onChange != null) {
            props.onChange(e)
          } else {
            inputChanged(e, touched)
          }
        }}
        sx={{
          '& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input': {
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }
        }}
        error={showError}
        InputProps={{
          ...baseProps,
          endAdornment: required && !disabled && (touched || inputHasValue)
            ? (
              <InputAdornment position='end'>
                {!showError
                  ? (
                    <CheckOutlined
                      sx={{
                        fontWeight: 700,
                        fontSize: '24px',
                        color: 'success.main',
                        marginRight: select ? '15px' : 'initial'
                      }}
                    />
                    )
                  : (
                    <ClearOutlined
                      sx={{
                        color: 'error.main',
                        fontWeight: 700,
                        fontSize: '24px',
                        marginRight: select ? '15px' : 'initial'
                      }}
                    />
                    )}
              </InputAdornment>
              )
            : (
                ''
              )
        }}
        inputProps={{
          maxLength: isNumber(limit) && limit
        }}
        select={select}
        variant={variant}
        fullWidth
        FormHelperTextProps={{ component: 'div' }}
        helperText={
          <Grid container>
            <Grid item xs>
              {showError ? <ErrorLabel label={errorLabel} /> : helperArray}
            </Grid>
            {isNumber(limit) && type !== 'countryCode' &&
              <Grid item sx={{ color: 'black !important', textAlign: 'right' }}>
                {countHelperText(inputValue, Number(limit))}
              </Grid>}
          </Grid>
              }
        type='text'
        onFocus={e => {
          if (startEditingValue === null) {
            setStartEditingValue(inputValue)
          }
          if (type === 'number' && inputValue === '0') {
            setDefaultZeroRemoved(true)
            setInputValue('')
          }
        }}
        onBlur={e => {
          setTouched(true)
          setStartEditingValue(null)
          if (type === 'number' && !inputHasValue && defaultZeroRemoved) {
            setDefaultZeroRemoved(false)
            setInputValue('0')
          } else if (defaultZeroRemoved) {
            setDefaultZeroRemoved(false)
          }
          e.stopPropagation()
          e.preventDefault()

          if (timer != null) {
            clearTimeout(timer)
            handleChange(e.currentTarget.value)
          }
        }}
        disabled={disabled}
        {...props}
        multiline={multiline}
        style={{ wordBreak: 'break-word', ...style }}
      >
        {select &&
          <MenuItem disabled value=''>
            <Typography sx={{ fontWeight: 400 }}>
              <Trans>SELECT_ONE</Trans>
            </Typography>
          </MenuItem>}
        {children}
      </TextField>
    </>
  )

  if (actionIconButton != null) {
    return (
      <Grid
        container
        direction='row'
        justifyContent='center'
        alignItems='flex-start'
        gap='8px'
      >
        <Grid item xs>
          {toReturn}
        </Grid>
        <Grid item sx={{ paddingTop: '8px' }}>
          {actionIconButton}
        </Grid>
      </Grid>
    )
  } else {
    return toReturn
  }
}
